import React, { useEffect, useState } from 'react';
import { useToast } from '@contexts/Toast';
import { errorMessage } from '@helpers/Constants';
import { EditUserModalProps, Role, RoleUser } from '@helpers/Models';
import { Axios } from '@helpers/ApiService';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { AxiosError } from 'axios';

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  user,
}) => {
  const { showToastMessage } = useToast();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [duplicateEmailError, setDuplicateEmailError] = useState('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<RoleUser[]>([]);
  const [rolePermissions, setRolePermissions] = useState<number[]>([]);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && roles.length > 0) {
      setName(user.name);
      // 🔽 Convert email to lowercase when populating from user data
      setEmail(user.email.toLowerCase());
      setEmailError('');
      const assignedRoles: number[] = [];
      user.roles.forEach((userRole) => {
        assignedRoles.push(Number(userRole.id));
      });
      setRolePermissions(assignedRoles);
    }
  }, [isOpen, roles, user]);

  const fetchRoles = async (): Promise<void> => {
    try {
      const [rolesResponse, usersResponse] = await Promise.all([
        Axios.post(ApiEndpoints.FETCH_ROLES, {}),
        Axios.post(ApiEndpoints.FETCH_USER_ROLES, {}),
      ]);

      setRoles(rolesResponse.data.data);
      setUsers(usersResponse.data.data);
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_MODULES, 'error');
    }
  };

  const hasDuplicateEmail = (value: string): boolean => {
    const normalizedEmail = value.trim().toLowerCase();
    if (!normalizedEmail) return false;

    return users.some(
      (existingUser) =>
        existingUser.id !== user.id &&
        existingUser.email.trim().toLowerCase() === normalizedEmail,
    );
  };

  const handleCheckboxChange = (roleId: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (event.target.checked) {
        setRolePermissions([...rolePermissions, roleId]);
      } else {
        setRolePermissions(rolePermissions.filter((id) => id !== roleId));
      }
    };
  };

  const validateEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // 🔽 Convert to lowercase and clear errors
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase(); // <-- force lowercase
    setEmail(value);

    if (emailError) {
      setEmailError('');
    }

    if (duplicateEmailError) {
      setDuplicateEmailError('');
    }

    const trimmedValue = value.trim();
    if (trimmedValue && validateEmail(trimmedValue) && hasDuplicateEmail(value)) {
      setDuplicateEmailError(errorMessage.USER_ALREADY_EXISTS);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[a-zA-Z\s]*$/.test(value)) {
      setName(value);
      if (value.trim()) {
        setNameError('');
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();

    let isValid = true;

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError('');
    }

    if (hasDuplicateEmail(email)) {
      setDuplicateEmailError(errorMessage.USER_ALREADY_EXISTS);
      isValid = false;
    } else {
      setDuplicateEmailError('');
    }

    if (!name.trim()) {
      setNameError('Please enter a valid name.');
      isValid = false;
    } else {
      setNameError('');
    }

    if (!isValid) return;

    try {
      const payload = {
        name: name.trim(),
        email: email.trim(), // email already lowercase
        roleId: rolePermissions,
        id: user.id,
      };

      const response = await Axios.post('/user/roles/upsert', payload);
      onClose();
      setName('');
      setEmail('');
      setEmailError('');
      setDuplicateEmailError('');
      setNameError('');
      setRolePermissions([]);
      showToastMessage(response.data.message, 'success');
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;

      if (
        axiosError.response?.data?.message === errorMessage.USER_ALREADY_EXISTS
      ) {
        setDuplicateEmailError(errorMessage.USER_ALREADY_EXISTS);
        return;
      }

      showToastMessage(errorMessage.ERROR_CREATING_ROLE, 'error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center border-b pb-3">
          <h2 className="text-lg font-bold">Edit</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="Name"
              className="mt-1 block w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-primary"
              required
            />
            {nameError && (
              <p className="text-red-500 text-sm mt-1">{nameError}</p>
            )}
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Email *
            </label>
            <input
              type="text"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email"
              className={`mt-1 block w-full border rounded-md p-2 text-sm ${emailError || duplicateEmailError
                ? 'border-red-500'
                : 'border-gray-300'
                } focus:outline-primary`}
              required
            />
            {emailError && (
              <p className="text-red-500 text-xs mt-1">{emailError}</p>
            )}
            {duplicateEmailError && (
              <p className="text-red-500 text-xs mt-1">{duplicateEmailError}</p>
            )}
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Roles *
            </label>

            <div className="grid grid-cols-2 gap-2 mt-3 border p-2 pt-4 border-gray-300 rounded-md">
              {roles.map((role: Role) => (
                <label
                  key={role.id}
                  className="flex items-center justify-between mr-4 mb-4"
                >
                  <span className="mr-2 text-sm font-semibold">
                    {role.name}
                  </span>
                  <input
                    type="checkbox"
                    className="w-auto h-auto rounded accent-primary"
                    onChange={handleCheckboxChange(Number(role.id))}
                    checked={rolePermissions.includes(Number(role.id))}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="text-gray-600 px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                !name ||
                !email ||
                !rolePermissions.length ||
                !!emailError ||
                !!nameError ||
                !!duplicateEmailError
              }
              className={`px-6 py-2 h-10 rounded-md text-white ${name &&
                email &&
                rolePermissions.length &&
                !emailError &&
                !nameError &&
                !duplicateEmailError
                ? 'bg-primary'
                : 'bg-gray-400'
                }`}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;